/**
 * dview: widgets that draw one figure: a km position, a key figure, a length on a scale,
 * a symbol, and equipment tiles. See dim-semantics src/shapes/common/dview.ttl.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TjnSignal } from '@bn-apps/dim-ui-react/tjn';
import { shortenUri } from '@hooks/useEntity';
import { linkedOfKind, type LinkedItem } from '@services/entityView.service';
import { conceptOf, paragraphOf, type ConceptIndex } from '@services/vocabulary.service';
import { expandUri } from '@utils/prefix.utils';
import { getIntlLocale } from '@utils/locale.utils';
import { pickLang, setting, settingList, type ViewPropertyShape } from '@utils/viewShapes.utils';
import type { EntityObject } from '@/types';
import { ERA } from '../page';
import { formatKm, localName, num, roleMap, words, type Lane, type Segment } from '../linearLocation';
import { Glyph } from './alongTrack';
import { signValue } from './signValue';
import { TjnPicture, tjnByCode } from './tjn';
import tjnStyles from './tjn.module.scss';
import { FitGlyph } from './legend';
import { Chip } from './chip';
import { SectionSummary, sectionMap, sectionsFor } from './sections';
import type { Widget, WidgetContext } from './types';
import { UriLink, uriText } from './values';
import styles from './widgets.module.scss';

const numbers = (values: EntityObject[]) =>
  values.map((value) => Number(value.value)).filter((value) => Number.isFinite(value));

/** Values of the row another row names by dview:key. */
function keyedValues(context: WidgetContext, key: string | undefined): EntityObject[] {
  const row = key ? context.model?.rows[key] : undefined;
  return row ? (context.model?.values[row.id] ?? []) : [];
}

/** The page's own label and km, for the value on its TJN sign. */
function pageData(context: WidgetContext): { label?: string; km: number | null } {
  return { label: context.model && context.resolved[context.model.uri]?.[0], km: num(keyedValues(context, 'at-km')) };
}

export function aggregate(property: ViewPropertyShape, values: EntityObject[]): number | null {
  const all = numbers(values);
  if (all.length === 0) return null;
  switch (setting(property, 'aggregate')) {
    case 'max':
      return Math.max(...all);
    case 'min':
      return Math.min(...all);
    case 'min-abs': {
      const nonZero = all.map(Math.abs).filter((value) => value > 0);
      return nonZero.length > 0 ? Math.min(...nonZero) : null;
    }
    case 'max-abs':
      return all.reduce((best, value) => (Math.abs(value) > Math.abs(best) ? value : best));
    case 'count':
      return all.length;
    default:
      return all[0];
  }
}

export function useNumberFormat(): (value: number, digits?: number) => string {
  const { i18n } = useTranslation();
  const locale = getIntlLocale(i18n.language);
  return (value, digits = 2) => new Intl.NumberFormat(locale, { maximumFractionDigits: digits }).format(value);
}

/** dview:KmViewer: metres along the line as km 18+428. */
export const KmViewer: Widget = ({ values }) => {
  const metres = num(values);
  return metres === null ? null : <span className={styles.km}>km {formatKm(metres)}</span>;
};

/**
 * Overlapping sections (speed profiles, and in the data also a raised and a lowered speed on the
 * same metres) as one line: the highest value at each point.
 */
function envelope(segments: Segment[]): { from: number; to: number; value: number }[] {
  const cuts = [...new Set(segments.flatMap((segment) => [segment.from, segment.to]))].sort((a, b) => a - b);
  const out: { from: number; to: number; value: number }[] = [];
  for (let i = 0; i < cuts.length - 1; i++) {
    const covering = segments.filter((segment) => segment.from <= cuts[i] && segment.to >= cuts[i + 1]);
    if (covering.length === 0) continue;
    const value = Math.max(...covering.map((segment) => segment.value as number));
    const last = out[out.length - 1];
    if (last && last.to === cuts[i] && last.value === value) last.to = cuts[i + 1];
    else out.push({ from: cuts[i], to: cuts[i + 1], value });
  }
  return out;
}

/** A lane drawn small: a step line for numbers (basis profile only), with breaks where there is no data. */
export function Spark({ lane, from, to }: { lane: Lane; from: number; to: number }): JSX.Element | null {
  const segments = envelope(
    lane.segments.filter((segment) => segment.value !== null)
  );
  if (segments.length === 0 || to <= from) return null;
  const values = segments.map((segment) => segment.value as number);
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const W = 120;
  const H = 24;
  const x = (km: number) => ((km - from) / (to - from)) * W;
  const y = (value: number) => (hi === lo ? H / 2 : H - 3 - ((value - lo) / (hi - lo)) * (H - 6));
  let d = '';
  let lastEnd: number | null = null;
  for (const segment of segments) {
    const y1 = y(segment.value as number);
    // Join to the step before when they touch; a break otherwise shows the gap.
    d += lastEnd !== null && segment.from - lastEnd < (to - from) / 200 ? `V${y1}` : `M${x(segment.from)} ${y1}`;
    d += `H${Math.max(x(segment.to), x(segment.from) + 1)}`;
    lastEnd = Math.max(lastEnd ?? segment.to, segment.to);
  }
  return (
    <svg className={styles.spark} viewBox={`0 0 ${W} ${H}`} aria-hidden="true" preserveAspectRatio="none">
      <path d={d} className={styles.sparkLine} />
    </svg>
  );
}

/** A gradient as a slope: rising or falling at its angle (exaggerated), with "1 m per N m". */
export function Slope({ permille }: { permille: number }): JSX.Element {
  const angle = Math.max(-30, Math.min(30, permille * 1.5));
  const rise = Math.tan((angle * Math.PI) / 180) * 40;
  return (
    <svg className={styles.picture} width="48" height="24" viewBox="0 0 48 24" aria-hidden="true">
      <path d="M4 20 H44" className={styles.pictureBase} />
      <path d={`M4 ${12 + rise / 2} L44 ${12 - rise / 2}`} className={styles.pictureSlope} />
    </svg>
  );
}

/** A curve as an arc of its radius (tighter is rounder), with the centre marked. */
export function Arc({ radius }: { radius: number }): JSX.Element {
  const bend = Math.max(2, Math.min(16, 4000 / Math.abs(radius)));
  return (
    <svg className={styles.picture} width="48" height="24" viewBox="0 0 48 24" aria-hidden="true">
      <path d={`M4 21 Q24 ${21 - 2 * bend} 44 21`} className={styles.pictureCurve} />
      <path d={`M24 ${21 - bend} L24 23`} className={styles.pictureBase} strokeDasharray="2 2" />
    </svg>
  );
}

/** A length on a bar with metre ticks at a round step (dview:picture "ruler"). */
export function Ruler({ metres }: { metres: number }): JSX.Element | null {
  const format = useNumberFormat();
  if (!(metres > 0)) return null;
  const W = 120;
  const raw = metres / 3;
  const power = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 5, 10].map((factor) => factor * power).find((candidate) => candidate >= raw) ?? raw;
  const ticks = Array.from({ length: Math.floor(metres / step) + 1 }, (_, index) => index * step);
  const x = (m: number) => 2 + (m / metres) * (W - 4);
  return (
    <svg className={styles.ruler} width={W} height="26" viewBox={`0 0 ${W} 26`} aria-hidden="true">
      <rect x="2" y="2" width={W - 4} height="7" rx="2" className={styles.rulerBar} />
      {ticks.map((m) => (
        <g key={m}>
          <path d={`M${x(m)} 9 V13`} className={styles.pictureBase} />
          <text x={x(m)} y="23" textAnchor={m === 0 ? 'start' : 'middle'} className={styles.rulerText}>
            {format(m, 0)}
          </text>
        </g>
      ))}
    </svg>
  );
}

/** dview:KeyFigureViewer: one number with its unit, and its picture (dview:picture: steps, slope, curve). */
export const KeyFigureViewer: Widget = ({ property, values, context }) => {
  const { t } = useTranslation();
  const format = useNumberFormat();
  const value = aggregate(property, values);
  if (value === null) return null;
  const unit = setting(property, 'unit') ?? '';
  const lane = context.stretch?.lanes.find((candidate) => candidate.key === setting(property, 'spark'));
  const kind = setting(property, 'picture');
  const sign = setting(property, 'aggregate') === 'max-abs' && value > 0 ? '+' : '';
  const picture =
    kind === 'slope' ? (
      <Slope permille={value} />
    ) : kind === 'curve' ? (
      <Arc radius={value} />
    ) : kind === 'ruler' ? (
      <Ruler metres={value} />
    ) : lane && context.stretch ? (
      <span className={styles.inlineSpark}>
        <Spark lane={lane} from={context.stretch.from} to={context.stretch.to} />
      </span>
    ) : null;
  const hint =
    kind === 'slope' && value !== 0
      ? t('entity.template.figure.slope', { metres: format(1000 / Math.abs(value), 0) })
      : null;
  if (context.compact) {
    return (
      <span className={styles.inlineFigure}>
        {context.pictures && picture}
        <span>
          {sign}
          {format(value, 1)} {unit}
        </span>
        {context.pictures && hint && <span className={styles.symbolContext}>{hint}</span>}
      </span>
    );
  }
  return (
    <span className={styles.keyFigure}>
      <span className={styles.keyFigureValue}>
        {sign}
        {format(value, 2)}
        {unit && <span className={styles.unit}> {unit}</span>}
      </span>
      {picture}
      {hint && <span className={styles.symbolContext}>{hint}</span>}
    </span>
  );
};

/** dview:ScaleBarViewer: a length against reference marks such as "0.4 Passenger train". */
export const ScaleBarViewer: Widget = ({ property, values, context }) => {
  const format = useNumberFormat();
  const length = num(keyedValues(context, setting(property, 'value'))) ?? num(values);
  if (length === null) return null;
  const unit = setting(property, 'unit') ?? '';
  const marks = (setting(property, 'marks') ?? '')
    .split(';')
    .map((part) => part.trim().match(/^([\d.]+)\s+(.+)$/))
    .filter((match): match is RegExpMatchArray => Boolean(match))
    .map((match) => ({ at: Number(match[1]), label: match[2] }));
  const text = (
    <span>
      {format(length, 3)} {unit}
    </span>
  );
  if (!context.pictures) return text;
  const scale = Math.max(length, ...marks.map((mark) => mark.at)) * 1.1;
  // Marks closer than this share of the bar would overlap: only the first keeps its label.
  const labelled = marks.filter((mark, index) => index === 0 || (mark.at - marks[index - 1].at) / scale > 0.25);
  return (
    <span className={styles.scaleBar}>
      {text}
      <span className={styles.scaleTrack} aria-hidden="true">
        <span className={styles.scaleFill} style={{ width: `${(length / scale) * 100}%` }} />
        {marks.map((mark) => (
          <span key={mark.label} className={styles.scaleMark} style={{ left: `${(mark.at / scale) * 100}%` }} title={mark.label}>
            {labelled.includes(mark) && <span className={styles.scaleMarkLabel}>{mark.label}</span>}
          </span>
        ))}
      </span>
    </span>
  );
};

const TRACK_TYPES = ['mainTrack', 'secondaryTrack', 'sidingTrack', 'connectingTrack'];

const TRACK_TYPE_PATHS: Record<string, string> = {
  mainTrack: 'M2 9 H30',
  secondaryTrack: 'M6 13 L12 6 H24 L30 13',
  sidingTrack: 'M6 13 L12 6 H23 M24 2 V10',
  connectingTrack: 'M2 15 L10 5 H30',
};

/** Small pictures for a value (board 6): the track types side by side with this one marked,
 * the direction as an arrow strip, the gauge as a rail profile. */
export function SymbolPicture({ symbol, value, size = 20 }: { symbol: string | undefined; value: string; size?: number }): JSX.Element | null {
  if (symbol === 'direction') {
    const both = value === 'both';
    const reverse = value === 'reverse';
    return (
      <svg className={styles.picture} width="56" height="20" viewBox="0 0 56 20" aria-hidden="true">
        <path d="M2 16 H54" className={styles.pictureBase} />
        <path d="M10 8 H46" className={styles.pictureStroke} />
        {(!reverse || both) && <path d="M40 3 L46 8 L40 13" className={styles.pictureStroke} />}
        {(reverse || both) && <path d="M16 3 L10 8 L16 13" className={styles.pictureStroke} />}
      </svg>
    );
  }
  if (symbol === 'side' && (value === 'left' || value === 'right')) {
    // Two rails seen from above, looking towards higher km (left to right): left is the upper side
    const y = value === 'left' ? 4 : 18;
    return (
      <svg className={styles.picture} width="44" height="22" viewBox="0 0 44 22" aria-hidden="true">
        <path d="M2 9 H42 M2 13 H42" className={styles.pictureBase} />
        <path d="M34 11 H40 M37 8 L40 11 L37 14" className={styles.pictureMuted} />
        <circle cx="20" cy={y} r="3" className={styles.pictureDot} />
      </svg>
    );
  }
  if (symbol === 'structure' && (value === 'bridge' || value === 'tunnel')) {
    // The hero's glyph, centred on the text beside it: small in a row, larger as a group's picture
    return <FitGlyph symbol={value} size={size} />;
  }
  if (symbol === 'loading-gauge') {
    // Schematic: the outline of a loading gauge in general, not the profile of this one
    return (
      <svg className={styles.picture} width="26" height="30" viewBox="0 0 26 30" aria-hidden="true">
        <path d="M4 28 V10 L9 3 H17 L22 10 V28" className={styles.pictureGauge} />
        <path d="M1 28 H25" className={styles.pictureBase} />
      </svg>
    );
  }
  if (symbol === 'gauge') {
    return (
      <svg className={styles.picture} width="44" height="20" viewBox="0 0 44 20" aria-hidden="true">
        <path d="M4 17 H40" className={styles.pictureBase} />
        <path d="M8 4 V16 H36 V4" className={styles.pictureStroke} />
      </svg>
    );
  }
  if (symbol === 'track-type') {
    return (
      <span className={styles.trackTypes} aria-hidden="true">
        {TRACK_TYPES.map((type) => (
          <svg key={type} width="32" height="18" viewBox="0 0 32 18" className={styles.trackType} data-current={type === value || undefined}>
            {type !== 'mainTrack' && <path d="M2 13 H30" className={styles.pictureBase} />}
            <path d={TRACK_TYPE_PATHS[type]} className={type === value ? styles.pictureMain : styles.pictureMuted} />
          </svg>
        ))}
      </span>
    );
  }
  return null;
}

/** Values that are notations of one concept (a switch type's code and its BaneData name) count once. */
export function onePerConcept<T extends { value: string }>(values: T[], concepts: ConceptIndex | undefined, scheme: string | undefined): T[] {
  const seen = new Set<string>();
  return values.filter((value) => {
    const key = conceptOf(concepts, scheme, value.value)?.iri ?? value.value;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** dview:SymbolViewer: a value with its picture (dview:symbol) and a line of context, on one line. */
export const SymbolViewer: Widget = ({ property, values, context }) => {
  const { t } = useTranslation();
  const shown = keyedValues(context, setting(property, 'value'));
  const items = onePerConcept(shown.length > 0 ? shown : values, context.concepts, setting(property, 'scheme'));
  if (items.length === 0) return null;
  const symbol = setting(property, 'symbol');
  // dview:symbolFor: a value drawn with one of the hero's glyphs (e.g. §8-10 as a main signal)
  const symbolOf = roleMap(property, 'symbolFor');
  return (
    <span className={styles.symbols}>
      {items.map((value) => {
        const concept = conceptOf(context.concepts, setting(property, 'scheme'), value.value);
        // dview:show "paragraph": only the code, linked to its paragraph (a type designator's entry)
        if (concept && setting(property, 'show') === 'paragraph') {
          return <Chip key={value.value} iri={concept.iri} href={concept.page ?? undefined} icon="rule" label={value.value} context={context} />;
        }
        // A concept without a page to read (a switch type, not a rule paragraph) is its name and its picture
        if (concept && !concept.page) {
          return (
            <span key={value.value} className={styles.symbol}>
              {context.pictures && concept.symbol && (
                <svg className={styles.picture} width="26" height="24" viewBox="0 0 26 24" aria-hidden="true">
                  <Glyph symbol={concept.symbol} x={13} y={21} />
                </svg>
              )}
              <span className={styles.symbolValue}>{pickLang(concept.label, context.language) ?? value.value}</span>
            </span>
          );
        }
        if (concept) {
          return (
            <span key={value.value} className={styles.symbol}>
              {context.pictures && <TjnPicture key={value.value} concept={concept} code={value.value} value={signValue(value.value, pageData(context))} className={styles.tjnSmall} />}
              <Chip iri={concept.iri} href={concept.page ?? undefined} icon="rule" label={pickLang(concept.label, context.language) ?? value.value} context={context} />
              {paragraphOf(concept) && paragraphOf(concept) !== value.value && (
                <code className={styles.symbolContext}>{value.value}</code>
              )}
            </span>
          );
        }
        const isIri = value.type === 'uri';
        const text = isIri ? uriText(value.value, context) : t(`entity.template.symbol.${symbol}.${value.value}`, value.value);
        const hintKey = isIri ? localName(value.value) : value.value;
        const tjn = setting(property, 'scheme') && !isIri ? tjnByCode(value.value) : null;
        return (
          <span key={value.value} className={styles.symbol}>
            {context.pictures && tjn && (
              <TjnSignal
                signal={tjn}
                value={signValue(tjn, pageData(context))}
                label=""
                size={30}
                className={`${tjnStyles.tjn} ${styles.tjnSmall}`}
              />
            )}
            {context.pictures && !tjn && symbolOf(value.value) && (
              <svg className={styles.picture} width="18" height="24" viewBox="0 0 18 24" aria-hidden="true">
                <Glyph symbol={symbolOf(value.value)!} x={9} y={23} />
              </svg>
            )}
            {context.pictures && !tjn && !symbolOf(value.value) && <SymbolPicture symbol={symbol} value={value.value} size={context.compact ? 20 : 32} />}
            <span className={styles.symbolValue}>{text}</span>
            {context.pictures && (
              <span className={styles.symbolContext}>{t(`entity.template.symbol.${symbol}.${hintKey}Hint`, '')}</span>
            )}
          </span>
        );
      })}
    </span>
  );
};

type FacilityState = 'yes' | 'no' | 'notApplicable' | 'unknown';

const FACILITY_MARK: Record<FacilityState, string> = { yes: '✓', no: '✕', notApplicable: '–', unknown: '?' };

/** dview:FacilitiesViewer: four-state tiles, one per row key in dview:facility, each listing its objects as links. */
const Facilities = ({ property, context }: Parameters<Widget>[0]): JSX.Element | null => {
  const { t } = useTranslation();
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [linked, setLinked] = useState<LinkedItem[]>([]);
  const model = context.model;
  // Everything on the entity that is a dview:others (e.g. any functional infrastructure) gets a tile
  // per class, unless a row already draws that class or dview:except names it.
  const step = property.path.kind === 'sequence' ? property.path.steps[0] : property.path;
  const predicate = step.kind === 'inverse' && step.path.kind === 'predicate' ? step.path.iri : null;
  const others = setting(property, 'others');
  const kind = others ? expandUri(others, context.prefixes) : null;
  const uri = model?.uri;
  useEffect(() => {
    if (!uri || !predicate || !kind) return;
    let cancelled = false;
    linkedOfKind(uri, predicate, kind)
      .then((items) => !cancelled && setLinked(items))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [uri, predicate, kind]);
  if (!model) return null;
  const drawn = new Set(model.allProperties.map((row) => row.classIri).filter(Boolean));
  const except = settingList(property, 'except');
  const extra = [...new Set(linked.map((item) => item.classIri))].filter(
    (iri) => !drawn.has(iri) && !except.includes(shortenUri(iri, context.prefixes))
  );
  const labels = Object.fromEntries(linked.filter((item) => item.label).map((item) => [item.iri, item.label!]));
  const otherTiles = extra.map((classIri) => {
    const items = linked.filter((item) => item.classIri === classIri).map((item) => item.iri);
    const name = context.resolved[classIri]?.[0] ?? words(localName(classIri));
    return { key: classIri, label: name.charAt(0).toUpperCase() + name.slice(1), state: 'yes' as FacilityState, count: items.length, items };
  });
  const tiles = settingList(property, 'facility').flatMap((key) => {
    const row = model.rows[key];
    if (!row) return [];
    const items = [
      ...(model.values[row.id] ?? []).filter((value) => value.type === 'uri').map((value) => value.value),
      ...(model.tables[row.id]?.items.map((item) => item.iri) ?? []),
    ];
    const count = items.length;
    const rinf = setting(row, 'rinf');
    const notApplicable = rinf ? (context.rinf?.get(`${ERA}notApplicable`) ?? []).some((value) => value.value === rinf) : false;
    const state: FacilityState = count > 0 ? 'yes' : notApplicable ? 'notApplicable' : 'unknown';
    const label = pickLang(row.name, context.language) ?? key;
    return [{ key, label: label.charAt(0).toUpperCase() + label.slice(1), state, count, items: [...new Set(items)] }];
  });
  if (tiles.length + otherTiles.length === 0) return null;
  const tile = (entry: { key: string; label: string; state: FacilityState; count: number; items: string[] }) => {
    const all = open.has(entry.key);
    return (
      <li key={entry.key} className={styles.facility} data-state={entry.state}>
        <span className={styles.facilityHead}>
          <span className={styles.facilityMark} aria-hidden="true">
            {FACILITY_MARK[entry.state]}
          </span>
          <span className={styles.facilityLabel}>{entry.label}</span>
          <span className={styles.facilityState}>
            {entry.state === 'yes' ? entry.count : t(`entity.template.facility.${entry.state}`)}
            {entry.state === 'notApplicable' && ' (RINF)'}
          </span>
        </span>
        {entry.items.length > 0 && (
          <span className={styles.facilityItems}>
            {(all ? entry.items : entry.items.slice(0, 4)).map((iri) => (
              <UriLink key={iri} uri={iri} context={context}>
                {labels[iri]}
              </UriLink>
            ))}
            {entry.items.length > 4 && (
              <button
                type="button"
                className={styles.facilityMore}
                aria-expanded={all}
                onClick={() => {
                  const next = new Set(open);
                  if (all) next.delete(entry.key);
                  else next.add(entry.key);
                  setOpen(next);
                }}
              >
                {all ? t('entity.template.facts.showFewer') : `+${entry.items.length - 4}`}
              </button>
            )}
          </span>
        )}
      </li>
    );
  };
  // The group's dview:sections splits the tiles under the groups it names; the rest last
  const sectionOf = sectionMap(context, property.groupId);
  const all = [...tiles, ...otherTiles];
  if (sectionOf.size === 0) return <ul className={styles.facilities}>{all.map(tile)}</ul>;
  const sections = sectionsFor(context, all.map((entry) => sectionOf.get(entry.key) ?? ''), t('entity.template.facility.other'));
  if (context.summary)
    return (
      <SectionSummary
        sections={sections.map((section) => ({
          ...section,
          count: all.filter((entry) => (sectionOf.get(entry.key) ?? '') === section.key).reduce((sum, entry) => sum + entry.count, 0),
        }))}
      />
    );
  if (context.sectionCard) {
    const card = context.sectionCard;
    return (
      <>
        {sections.map((section) => (
          <section key={section.key} className={card.card} aria-label={section.title}>
            <h3 className={card.title}>{section.title}</h3>
            <ul className={styles.facilities}>{all.filter((entry) => (sectionOf.get(entry.key) ?? '') === section.key).map(tile)}</ul>
          </section>
        ))}
      </>
    );
  }
  return (
    <div className={styles.facilitySections}>
      {sections.map((section) => (
        <section key={section.key} className={styles.facilitySection}>
          <h4 className={styles.facilitySectionTitle}>{section.title}</h4>
          <ul className={styles.facilities}>{all.filter((entry) => (sectionOf.get(entry.key) ?? '') === section.key).map(tile)}</ul>
        </section>
      ))}
    </div>
  );
};

export const FacilitiesViewer: Widget = (props) => <Facilities {...props} />;

/** dview:ScaleViewer: every step of dview:steps with the value's step lit; a value off the scale as text. */
export const ScaleViewer: Widget = ({ property, values, context }) => {
  const { t } = useTranslation();
  const value = values[0]?.value;
  if (!value) return null;
  const steps = settingList(property, 'steps');
  if (!context.pictures || !steps.includes(value)) return <span>{value}</span>;
  return (
    <span className={styles.steps} role="img" aria-label={t('entity.template.scale.aria', { value, steps: steps.join(', ') })}>
      {steps.map((step) => (
        <span key={step} className={styles.step} data-current={step === value || undefined}>
          {step}
        </span>
      ))}
    </span>
  );
};

/** The name of a linked document from its address: …/Beredskapskort-Jarlsbergtunnelen.aspx → "Beredskapskort Jarlsbergtunnelen". */
export function documentName(url: string): string {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }
  const last = parsed.pathname.split('/').filter(Boolean).pop();
  if (!last) return parsed.hostname;
  let name = last;
  try {
    name = decodeURIComponent(last);
  } catch {
    // keep the encoded name
  }
  return name.replace(/\.[a-z0-9]{2,5}$/i, '').replace(/[-_]+/g, ' ').trim() || parsed.hostname;
}

/** dview:YesNoViewer: Yes or No, with links to the documents in the dview:document row. */
export const YesNoViewer: Widget = ({ property, values, context }) => {
  const { t } = useTranslation();
  const value = values[0]?.value;
  if (!value) return null;
  const answer = ['yes', 'true'].includes(value.toLowerCase()) ? 'yes' : ['no', 'false'].includes(value.toLowerCase()) ? 'no' : null;
  const documents = keyedValues(context, setting(property, 'document')).filter((document) => /^https?:/.test(document.value));
  return (
    <span className={styles.yesNo}>
      <span className={styles.yesNoValue}>{answer ? t(`entity.template.yesNo.${answer}`) : value}</span>
      {documents.map((document) => (
        <a key={document.value} href={document.value} target="_blank" rel="noopener noreferrer" className={styles.document}>
          {documentName(document.value)} ↗
        </a>
      ))}
    </span>
  );
};
